import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import type { AuthUser } from '@common/types'
import { fetchCurrentUser, logout as apiLogout } from '@frontend/lib/api'

interface AuthContextValue {
  user: AuthUser | null
  loading: boolean
  login: () => void
  logout: () => Promise<void>
  clearAuth: () => void // For React Query to call on 401
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)

  // Check auth once on mount
  useEffect(() => {
    fetchCurrentUser()
      .then(setUser)
      .catch(() => setUser(null))
      .finally(() => setLoading(false))
  }, [])

  const login = useCallback(() => {
    window.location.href = '/api/auth/google'
  }, [])

  const logout = useCallback(async () => {
    try {
      await apiLogout()
    } catch (err) {
      console.error('Logout failed:', err)
    } finally {
      setUser(null)
    }
  }, [])

  const clearAuth = useCallback(() => {
    setUser(null)
  }, [])

  return <AuthContext.Provider value={{ user, loading, login, logout, clearAuth }}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
